/*
Copyright (C) 2022  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
import {
  ArrowRightLeft,
  CircleCheck,
  CircleX,
  CopyPlus,
  HardDrive,
  Info,
  LoaderCircle,
  Network,
  Trash2,
} from "lucide-react";

import { MetalHubDisk, MetalHubNic, MetalHubServer } from "@src/@types/MetalHub";
import CopyValue from "@src/components/ui/CopyValue";
import {
  ActionButton,
  ActionGroup,
  DetailsActions,
  DetailsPage,
  KeyValueGrid,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import {
  Cell,
  FlexCell,
  ListHeader,
  Mono,
  Muted,
  Num,
  Pill,
  Primary,
  Secondary,
  Stack,
  T9,
  formatDateTime,
  gridTemplate,
  timeAgo,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 160px;
  color: ${T9.slate500};
  font-size: 14px;
  svg {
    color: ${T9.slate400};
    animation: mhd-spin 1s linear infinite;
  }
  @keyframes mhd-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Table = styled.div`
  border-top: 1px solid ${T9.slate100};
  border-radius: 0 0 8px 8px;
  overflow-x: auto;
`;
const TableInner = styled.div<{ minWidth: number }>`
  min-width: ${props => props.minWidth}px;
`;
const Row = styled.div<{ template: string; sub?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.template};
  column-gap: 16px;
  align-items: center;
  padding: 0 8px 0 16px;
  min-height: ${props => (props.sub ? 44 : 52)}px;
  border-bottom: 1px solid ${T9.slate100};
  background: ${props => (props.sub ? "var(--t9-bg-surface)" : T9.slate50)};
  &:last-child {
    border-bottom: 0;
  }
`;
const SubName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-left: 40px;
`;
const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: ${T9.slate300};
  flex-shrink: 0;
`;
const Addresses = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  padding: 6px 0;
`;
const Muted2 = styled.span`
  color: ${T9.slate500};
`;

const DISK_COLUMNS: ListColumn[] = [
  { key: "name", label: "Disk / partition", width: "minmax(240px, 2fr)" },
  { key: "size", label: "Size", width: "110px", align: "right" },
  { key: "sectors", label: "Sectors", width: "120px", align: "right" },
  { key: "start", label: "Start sector", width: "120px", align: "right" },
  { key: "end", label: "End sector", width: "120px", align: "right" },
  { key: "uuid", label: "Partition UUID", width: "minmax(200px, 1.4fr)" },
];
const NIC_COLUMNS: ListColumn[] = [
  { key: "name", label: "Interface", width: "minmax(180px, 1.2fr)" },
  { key: "mac", label: "MAC address", width: "180px" },
  { key: "ips", label: "IP addresses", width: "minmax(240px, 2fr)" },
  { key: "type", label: "Type", width: "140px" },
];

const GB = 1024 * 1024 * 1024;
const formatBytes = (bytes?: number | null) => {
  if (!bytes) {
    return null;
  }
  if (bytes < GB) {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }
  return `${(bytes / GB).toFixed(2)} GB`;
};

type Props = {
  server: MetalHubServer | null;
  loading: boolean;
  creatingReplica: boolean;
  creatingMigration: boolean;
  onCreateReplicaClick: () => void;
  onCreateMigrationClick: () => void;
  onDeleteClick: () => void;
};
@observer
class MetalHubServerDetailsContent extends React.Component<Props> {
  renderLoading() {
    return (
      <SectionCard testId="metalHubServerDetails-loading">
        <Loading>
          <LoaderCircle size={20} />
          Loading the On-prem server…
        </Loading>
      </SectionCard>
    );
  }

  renderButtons() {
    if (this.props.loading) {
      return null;
    }

    const creating = this.props.creatingReplica || this.props.creatingMigration;
    const inactive = !this.props.server?.active;
    const inactiveTitle = inactive ? "The server must be online to be transferred" : undefined;

    return (
      <DetailsActions>
        <ActionGroup>
          <ActionButton
            onClick={this.props.onCreateReplicaClick}
            disabled={creating || inactive}
            title={inactiveTitle}
          >
            {this.props.creatingReplica ? (
              <LoaderCircle size={16} className="spin" />
            ) : (
              <CopyPlus size={16} />
            )}
            {this.props.creatingReplica ? "Loading the wizard…" : "Create replica"}
          </ActionButton>
          <ActionButton
            variant="secondary"
            disabled={creating || inactive}
            title={inactiveTitle}
            onClick={this.props.onCreateMigrationClick}
          >
            {this.props.creatingMigration ? (
              <LoaderCircle size={16} className="spin" />
            ) : (
              <ArrowRightLeft size={16} />
            )}
            {this.props.creatingMigration ? "Loading the wizard…" : "Create migration"}
          </ActionButton>
        </ActionGroup>
        <ActionButton
          variant="danger"
          onClick={() => {
            this.props.onDeleteClick();
          }}
        >
          <Trash2 size={16} />
          Remove server
        </ActionButton>
      </DetailsActions>
    );
  }

  renderStatus(server: MetalHubServer) {
    return server.active ? (
      <Pill tone="success" icon={CircleCheck}>
        Online
      </Pill>
    ) : (
      <Pill tone="danger" icon={CircleX}>
        Offline
      </Pill>
    );
  }

  renderInfo() {
    if (this.props.loading || !this.props.server) {
      return null;
    }
    const server = this.props.server;
    const os = server.os_info?.os_name
      ? `${server.os_info.os_name} ${server.os_info.os_version || ""}`.trim()
      : null;
    return (
      <SectionCard
        title="Overview"
        icon={Info}
        testId="metalHubServerDetails-overview"
      >
        <KeyValueGrid
          columns={4}
          items={[
            { label: "Hostname", value: server.hostname ? this.renderValue(server.hostname) : null },
            { label: "Status", value: this.renderStatus(server) },
            { label: "Operating system", value: os ? this.renderValue(os) : null },
            { label: "Firmware", value: server.firmware_type || null },
            {
              label: "API endpoint",
              span: 2,
              mono: true,
              value: server.api_endpoint ? this.renderValue(String(server.api_endpoint)) : null,
            },
            {
              label: "CPU cores",
              value:
                server.physical_cores || server.logical_cores
                  ? `${server.physical_cores || "-"} physical, ${server.logical_cores || "-"} logical`
                  : null,
            },
            { label: "Memory", value: formatBytes(server.memory) },
            { label: "Created", value: server.created_at ? formatDateTime(server.created_at) : null },
            {
              label: "Last seen",
              value: server.updated_at ? (
                <span title={formatDateTime(server.updated_at)}>
                  {formatDateTime(server.updated_at)} <Muted2>· {timeAgo(server.updated_at)}</Muted2>
                </span>
              ) : null,
              span: 2,
            },
            { label: "ID", value: this.renderValue(String(server.id)), mono: true },
          ]}
        />
      </SectionCard>
    );
  }

  renderValue(value: string) {
    return <CopyValue value={value} maxWidth="100%" />;
  }

  /**
   * The partitions count logical sectors: when the physical sector size would make them larger
   * than the disk, they are 512 byte sectors.
   */
  partitionSectorSize(disk: MetalHubDisk) {
    const physical = disk.physical_sector_size || 0;
    const sectors = (disk.partitions || []).reduce((sum, p) => sum + (p.sectors || 0), 0);
    if (physical && disk.size && sectors * physical > disk.size) {
      return 512;
    }
    return physical;
  }

  renderPartitions(disk: MetalHubDisk, template: string) {
    const sectorSize = this.partitionSectorSize(disk);
    return (disk.partitions || []).map((partition, i) => {
      const size = partition.sectors * sectorSize;
      return (
        <Row
          key={partition.partition_uuid || `${disk.id}-${partition.name}-${i}`}
          template={template}
          sub
          data-test-id="metalHubServerDetails-partition"
        >
          <SubName>
            <Dot />
            <Stack>
              <Primary style={{ fontSize: 13 }}>{partition.name}</Primary>
              {partition.path ? <Secondary>{partition.path}</Secondary> : null}
            </Stack>
          </SubName>
          <Num>{sectorSize ? formatBytes(size) : <Muted>—</Muted>}</Num>
          <Num>{partition.sectors.toLocaleString("en-GB")}</Num>
          <Num>{partition.start_sector.toLocaleString("en-GB")}</Num>
          <Num>
            {partition.end_sector != null ? partition.end_sector.toLocaleString("en-GB") : <Muted>—</Muted>}
          </Num>
          <Cell title={partition.partition_uuid || ""}>
            {partition.partition_uuid ? <Mono>{partition.partition_uuid}</Mono> : <Muted>—</Muted>}
          </Cell>
          <div />
        </Row>
      );
    });
  }

  renderDisks() {
    if (this.props.loading || !this.props.server) {
      return null;
    }
    const disks = this.props.server.disks || [];
    const template = gridTemplate(DISK_COLUMNS);
    return (
      <SectionCard
        title={`Disks (${disks.length})`}
        icon={HardDrive}
        flush={disks.length > 0}
        testId="metalHubServerDetails-disks"
      >
        {disks.length ? (
          <Table>
            <TableInner minWidth={960}>
              <ListHeader columns={DISK_COLUMNS} />
              {disks.map((disk, i) => (
                <React.Fragment key={disk.id || disk.path || i}>
                  <Row template={template} data-test-id="metalHubServerDetails-disk">
                    <FlexCell gap={12}>
                      <IconTile icon={HardDrive} size={28} />
                      <Stack>
                        <Primary>{disk.name || disk.path}</Primary>
                        <Secondary>
                          {disk.path}
                          {disk.partitions?.length
                            ? ` · ${disk.partitions.length} partition${disk.partitions.length === 1 ? "" : "s"}`
                            : ""}
                          {disk.physical_sector_size ? ` · ${disk.physical_sector_size} B physical sectors` : ""}
                        </Secondary>
                      </Stack>
                    </FlexCell>
                    <Num>{formatBytes(disk.size) || <Muted>—</Muted>}</Num>
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                  </Row>
                  {this.renderPartitions(disk, template)}
                </React.Fragment>
              ))}
            </TableInner>
          </Table>
        ) : (
          <EmptyState compact icon={HardDrive} tone="neutral" title="No disks reported" />
        )}
      </SectionCard>
    );
  }

  renderNicRow(nic: MetalHubNic, template: string) {
    return (
      <Row key={nic.nic_name} template={template} sub data-test-id="metalHubServerDetails-nic">
        <FlexCell gap={12}>
          <IconTile icon={Network} size={28} tone="info" />
          <Primary>{nic.nic_name}</Primary>
        </FlexCell>
        <Cell>
          <Mono>{nic.mac_address || "—"}</Mono>
        </Cell>
        <Addresses>
          {nic.ip_addresses?.length ? (
            nic.ip_addresses.map(ip => (
              <Pill key={ip} tone="neutral">
                {ip}
              </Pill>
            ))
          ) : (
            <Muted>—</Muted>
          )}
        </Addresses>
        <Cell>{nic.interface_type || <Muted>—</Muted>}</Cell>
        <div />
      </Row>
    );
  }

  renderNics() {
    if (this.props.loading || !this.props.server) {
      return null;
    }
    const nics = this.props.server.nics || [];
    const template = gridTemplate(NIC_COLUMNS);
    return (
      <SectionCard
        title={`Network interfaces (${nics.length})`}
        icon={Network}
        flush={nics.length > 0}
        testId="metalHubServerDetails-nics"
      >
        {nics.length ? (
          <Table>
            <TableInner minWidth={820}>
              <ListHeader columns={NIC_COLUMNS} />
              {nics.map(nic => this.renderNicRow(nic, template))}
            </TableInner>
          </Table>
        ) : (
          <EmptyState compact icon={Network} tone="neutral" title="No network interfaces reported" />
        )}
      </SectionCard>
    );
  }

  render() {
    return (
      <DetailsPage>
        {this.renderInfo()}
        {this.renderDisks()}
        {this.renderNics()}
        {this.props.loading ? this.renderLoading() : null}
        {this.renderButtons()}
      </DetailsPage>
    );
  }
}

export default MetalHubServerDetailsContent;
